import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { OptionSymbol } from '@/components/ui/OptionSymbol';
import { useClosePosition } from '@/hooks/queries/usePositions';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { PositionWithLegs, CloseLegRequest } from '@steward/shared';

interface ClosePositionFormProps {
  position: PositionWithLegs;
  chainId: string;
}

interface FormValues {
  legs: Array<{
    legId: string;
    quantity: number;
    pricePerContract: number;
  }>;
  notes: string;
}

export function ClosePositionForm({ position, chainId }: ClosePositionFormProps) {
  const navigate = useNavigate();
  const closePosition = useClosePosition();

  const openLegs = position.legs.filter((leg) => leg.quantityRemaining > 0);

  const { register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: {
      legs: openLegs.map((leg) => ({
        legId: leg.id,
        quantity: leg.quantityRemaining,
        pricePerContract: 0,
      })),
      notes: '',
    },
  });

  const watchedLegs = watch('legs');

  const calculateNetPremium = () => {
    return watchedLegs.reduce((sum, watchedLeg, index) => {
      const originalLeg = openLegs[index];
      const value = watchedLeg.pricePerContract * watchedLeg.quantity * 100;
      // Closing action is opposite of opening
      const isClosingSell = originalLeg.action === 'BTO' || originalLeg.action === 'BTC';
      return sum + (isClosingSell ? value : -value);
    }, 0);
  };

  const onSubmit = async (data: FormValues) => {
    const legs: CloseLegRequest[] = data.legs
      .filter((leg) => leg.quantity > 0 && leg.pricePerContract > 0)
      .map((leg) => ({
        legId: leg.legId,
        quantity: leg.quantity,
        pricePerContract: leg.pricePerContract,
      }));

    if (legs.length === 0) return;

    await closePosition.mutateAsync({
      id: position.id,
      data: {
        legs,
        notes: data.notes || undefined,
      },
    });

    navigate(`/chains/${chainId}`);
  };

  const netPremium = calculateNetPremium();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card>
        <CardHeader>
          <CardTitle>Close Position</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Legs to close */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Legs to Close</h4>
            {openLegs.map((leg, index) => {
              const closingAction =
                leg.action === 'STO' ? 'BTC' :
                leg.action === 'BTO' ? 'STC' :
                leg.action === 'STC' ? 'BTO' : 'STO';

              return (
                <div
                  key={leg.id}
                  className="p-4 border border-gray-200 rounded-lg space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-500">{closingAction}</span>
                      <OptionSymbol symbol={leg.optionSymbol} compact />
                    </div>
                    <span className="text-sm text-gray-500">
                      {leg.quantityRemaining} remaining
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Quantity"
                      type="number"
                      min={1}
                      max={leg.quantityRemaining}
                      {...register(`legs.${index}.quantity`, {
                        valueAsNumber: true,
                        min: 1,
                        max: leg.quantityRemaining,
                      })}
                    />
                    <Input
                      label="Price per Contract"
                      type="number"
                      step="0.01"
                      min={0}
                      {...register(`legs.${index}.pricePerContract`, {
                        valueAsNumber: true,
                        min: 0,
                      })}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">Net Premium</span>
              <span
                className={cn(
                  'text-lg font-semibold',
                  netPremium >= 0 ? 'text-green-600' : 'text-red-600'
                )}
              >
                {formatCurrency(netPremium)}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {netPremium >= 0 ? 'Credit received' : 'Debit paid'}
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Add any notes about this close..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/chains/${chainId}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={closePosition.isPending}
              disabled={watchedLegs.every((leg) => leg.quantity === 0 || leg.pricePerContract === 0)}
            >
              Close Position
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
